import { useState } from 'react';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_PUBLIC_PRESET_NAME } from '../../../global';

interface ImageInfo {
  url: string;
  deleteToken?: string;
}

export function useImageUpload(initialImageUrl?: string | null) {
  const [state, setState] = useState({
    imageInfo: initialImageUrl
      ? {
          url: initialImageUrl
        }
      : (undefined as ImageInfo | undefined),
    error: undefined as string | undefined,
    uploading: false
  });
  function deleteImageIfPossible() {
    if (state.imageInfo?.deleteToken) {
      fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/delete_by_token?token=${state.imageInfo.deleteToken}`,
        {
          method: 'POST'
        }
      );
    }
  }
  function removeImage() {
    deleteImageIfPossible();
    setState({ imageInfo: undefined, error: undefined, uploading: false });
  }
  async function upload(image: File) {
    setState(s => ({
      ...s,
      uploading: true,
      error: undefined
    }));
    const data = new FormData();
    data.set('file', image);
    try {
      deleteImageIfPossible();
      const result = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload?upload_preset=${CLOUDINARY_PUBLIC_PRESET_NAME}`,
        {
          method: 'POST',
          body: data
        }
      ).then(
        response => response.json(),
        error => ({ error })
      );
      if (result.error && result.error.message) {
        setState(s => ({
          ...s,
          uploading: false,
          error: result.error.message
        }));
      } else {
        setState({
          imageInfo: {
            url: result.secure_url,
            deleteToken: result.delete_token
          },
          uploading: false,
          error: undefined
        });
      }
    } catch {
      setState(s => ({ ...s, uploading: false, error: 'Upload failed' }));
    }
  }
  return {
    imageUrl: state?.imageInfo?.url ?? null,
    uploading: state.uploading,
    error: state.error,
    upload,
    removeImage,
    uploadFromInput(event: React.ChangeEvent<HTMLInputElement>) {
      if (event.target.files?.length && event.target.files[0].type.startsWith('image/')) {
        upload(event.target.files[0]);
      } else {
        setState(s => ({
          ...s,
          uploading: false,
          error: 'Please select an image file.'
        }));
      }
    }
  };
}

export type ImageUpload = ReturnType<typeof useImageUpload>;
